import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, datasetLd } from '../seo';
import cities from '../data/cities.json';
import localitiesData from '../data/localities.json';

interface LocalityStats {
  city: { slug: string; name: string; state: string };
  locality: { slug: string; name: string; lat: number; lng: number };
  stats: {
    listings: {
      count: number;
      avg_hourly: number | null;
      avg_daily: number | null;
      avg_monthly: number | null;
    };
    by_vehicle_type: Record<string, number>;
  } | null;
  last_snapshot: { snapshot_date: string; p25: number | null; p50: number | null; p75: number | null } | null;
}

interface Locality {
  slug: string;
  name: string;
  lat: number;
  lng: number;
}

export default function LocalityPricePage() {
  const { city: citySlug = '', locality: localitySlug = '' } = useParams<{ city: string; locality: string }>();
  const [stats, setStats] = useState<LocalityStats | null>(null);
  const [locality, setLocality] = useState<Locality | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    const found = (localitiesData as { city: string; localities: Locality[] }[]).find(
      (g) => g.city === citySlug
    )?.localities.find((l) => l.slug === localitySlug);
    setLocality(found ?? null);
    if (found) {
      fetch(`/api/stats/locality/${citySlug}/${localitySlug}?lat=${found.lat}&lng=${found.lng}`)
        .then((r) => r.json())
        .then((j) => {
          if (j?.data) setStats(j.data);
        })
        .catch(() => undefined);
    }
    setUpdatedAt(new Date().toISOString());
  }, [citySlug, localitySlug]);

  const city = (cities as { slug: string; name: string; state: string }[]).find((c) => c.slug === citySlug);
  if (!city || !locality) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900">Locality not found</h1>
        <p className="text-gray-600 mt-2">No price data for {citySlug}/{localitySlug}.</p>
        <Link to="/prices" className="text-parksy-600 hover:underline mt-4 inline-block">
          Back to all prices →
        </Link>
      </div>
    );
  }

  const pageTitle = `Parking Prices in ${locality.name}, ${city.name} | Parksy`;
  const description = `Live, crowdsourced parking prices in ${locality.name}, ${city.name}. Monthly, daily, and hourly averages plus p25/p50/p75.`;
  const canonical = `https://parksy.rent/prices/${city.slug}/${locality.slug}`;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Prices', to: '/prices' },
          { label: city.name, to: `/prices/${city.slug}` },
          { label: locality.name },
        ]}
        pageTitle={pageTitle}
        pageDescription={description}
        extraSchemas={[
          organizationLd(),
          webSiteLd(),
          datasetLd({
            name: `${locality.name} parking prices`,
            description: `Live parking price data for ${locality.name}, ${city.name}.`,
            url: canonical,
            citySlug: city.slug,
            localitySlug: locality.slug,
            updatedAt,
            rowCount: stats?.stats?.listings.count,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Parking prices in {locality.name}</h1>
          <p className="text-gray-600">
            {city.name}, {city.state}. Updated continuously.
          </p>
        </header>

        {stats?.stats?.listings.count ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Stat label="Listings" value={String(stats.stats.listings.count)} />
              <Stat
                label="Avg ₹/month"
                value={stats.stats.listings.avg_monthly ? `₹${Math.round(stats.stats.listings.avg_monthly).toLocaleString('en-IN')}` : '—'}
              />
              <Stat
                label="Avg ₹/day"
                value={stats.stats.listings.avg_daily ? `₹${Math.round(stats.stats.listings.avg_daily).toLocaleString('en-IN')}` : '—'}
              />
              <Stat
                label="Avg ₹/hour"
                value={stats.stats.listings.avg_hourly ? `₹${Math.round(stats.stats.listings.avg_hourly).toLocaleString('en-IN')}` : '—'}
              />
            </div>

            {stats.last_snapshot && (
              <div className="bg-white border rounded-xl p-4 mb-6 text-sm">
                <h2 className="font-semibold text-gray-900 mb-2">
                  Last daily snapshot ({stats.last_snapshot.snapshot_date})
                </h2>
                <div className="flex flex-wrap gap-4 text-gray-700">
                  <span>p25: <strong>{stats.last_snapshot.p25 ? `₹${Math.round(stats.last_snapshot.p25).toLocaleString('en-IN')}` : '—'}</strong></span>
                  <span>p50 (median): <strong>{stats.last_snapshot.p50 ? `₹${Math.round(stats.last_snapshot.p50).toLocaleString('en-IN')}` : '—'}</strong></span>
                  <span>p75: <strong>{stats.last_snapshot.p75 ? `₹${Math.round(stats.last_snapshot.p75).toLocaleString('en-IN')}` : '—'}</strong></span>
                </div>
              </div>
            )}

            {Object.keys(stats.stats.by_vehicle_type ?? {}).length > 0 && (
              <div className="bg-white border rounded-xl p-4 mb-8 text-sm">
                <h2 className="font-semibold text-gray-900 mb-2">By vehicle type</h2>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.stats.by_vehicle_type).map(([vt, count]) => (
                    <span key={vt} className="bg-gray-50 border rounded-lg px-3 py-1 text-gray-700">
                      {vt}: <strong>{count}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h2 className="font-semibold text-amber-900 mb-1">No data yet for {locality.name}</h2>
            <p className="text-amber-800 text-sm mb-3">
              Pin what you currently pay for parking in {locality.name} — it takes 30 seconds and helps
              everyone in your neighbourhood.
            </p>
            <Link
              to="/"
              className="inline-block bg-parksy-600 hover:bg-parksy-700 text-white font-medium px-4 py-2 rounded-lg text-sm"
            >
              Open the map →
            </Link>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Data is crowdsourced and may contain outliers. Parksy filters reports and applies
          automatic anomaly detection.
        </p>
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border rounded-xl p-4 text-center">
      <div className="text-2xl font-bold text-parksy-600">{value}</div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{label}</div>
    </div>
  );
}
